// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import { Stack, useGlobalSearchParams, useRouter, useSegments } from 'expo-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';

import { useColorScheme } from '@/components/useColorScheme';
import { useI18n } from '@/i18n';
import { useUser } from '@/app/user-context';
import { HeaderUser } from '@/components/HeaderUser';
import { BottomMenu } from '@/components/BottomMenu';
import { getCurrentTourId } from '@/services/currentTour';
import { useRoundState } from '@/stores/roundState';
import { useDebugState } from '@/stores/debugState';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { useAuthFlow } from '@/hooks/layout/useAuthFlow';
import { useServiceHealth } from '@/hooks/layout/useServiceHealth';
import { useUserInfoSync } from '@/hooks/layout/useUserInfoSync';
import { useScoreAllHoles, type ScoreAllHolesResult, type ImportFixture } from '@/hooks/dev/useScoreAllHoles';
import { useRoundInfo, type RoundInfoResult } from '@/hooks/dev/useRoundInfo';
import { hasKeycloakRole } from '@/utils/keycloakRoles';
import { useManageableCourses } from '@/hooks/useManageableCourses';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';
import { ServiceStatusModal } from '@/components/layout/ServiceStatusModal';
import { DebugModal } from '@/components/layout/DebugModal';
import { createAppLogger } from '@/services/appLogger';
import { getSharedBffWebSocket, type BffInboundMessage } from '@/services/bffWebSocket';
import { getCoachMarkStatesForDebug, resetCoachMarksForDebug } from '@/services/coachMarks';
import appColors from '@/theme/colors';

type BugImageSelection = {
  file: File;
  fileName: string;
  contentType: string;
  size: number;
  previewUrl: string;
  uploadedUrl: string | null;
};

const COMPACT_WEB_HEADER_BREAKPOINT = 430;

export default function RootLayoutNav() {
  const colorScheme = useColorScheme();
  const { t } = useI18n();
  const { user, setUser } = useUser();
  const { width } = useWindowDimensions();
  const router = useRouter();
  const segments = useSegments();
  const { debug: debugParam } = useGlobalSearchParams<{ debug?: string }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const [bugReportDescription, setBugReportDescription] = useState('');
  const [bugReportImage, setBugReportImage] = useState<BugImageSelection | null>(null);
  const [bugReportSubmitting, setBugReportSubmitting] = useState(false);
  const [bugReportError, setBugReportError] = useState<string | null>(null);
  const [debugTourName, setDebugTourName] = useState<string | null>(null);
  const [debugRoundName, setDebugRoundName] = useState<string | null>(null);
  const [debugPlayerId, setDebugPlayerId] = useState<string | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugCoachMarks, setDebugCoachMarks] = useState<Array<{ key: string; shownCount: number; maxShows: number }>>([]);
  const [debugCoachMarksLoading, setDebugCoachMarksLoading] = useState(false);
  const topSegment = segments[0];
  const roundId = useRoundState((state) => state.roundId);
  const scorerOnlyMode = useRoundState((state) => state.scorerOnlyMode);
  const roundIdFromSegments = useMemo(() => {
    if (topSegment !== 'round') {
      return null;
    }
    const segmentValue = segments[1];
    return typeof segmentValue === 'string' && segmentValue.length > 0 ? segmentValue : null;
  }, [segments, topSegment]);
  const isHoleRoute = useMemo(() => {
    return topSegment === 'round' && segments[2] === 'hole';
  }, [segments, topSegment]);
  const effectiveRoundId = roundId ?? roundIdFromSegments;
  const debugEnabled = useDebugState((state) => state.enabled);
  const setDebugEnabled = useDebugState((state) => state.setEnabled);
  const [tourId, setTourId] = useState<string | null>(null);
  const [singleRoundTourIds, setSingleRoundTourIds] = useState<Record<string, boolean>>({});
  const logToBff = useMemo(
    () =>
      createAppLogger(apiBaseUrl, user?.token, {
        source: 'frontend-error',
      }),
    [apiBaseUrl, user?.token]
  );
  const { handleRedirect } = useAuthRedirect({ user, setUser });

  const { authChecked } = useAuthFlow({ user, setUser });

  const {
    canCheckServiceHealth,
    serviceStatusOpen,
    setServiceStatusOpen,
    serviceStatusMissing,
    serviceStatusCheckedAt,
    serviceStatusHasWarnings,
    serviceStatusFetchFailed,
    serviceStatusFlashOn,
    serviceStatusWarnings,
  } = useServiceHealth({ apiBaseUrl, user });
  const isInviteRoute = segments[0] === 'tour' && segments[1] === 'invite';
  const isPublicInviteView = isInviteRoute && !user?.token;
  const isPublicLandingView = !user?.token && (segments.length === 0 || topSegment === 'index');
  const isAnonymousMode = !user?.token;
  const compactWebHeader = Platform.OS === 'web' && width > 0 && width <= COMPACT_WEB_HEADER_BREAKPOINT;

  useEffect(() => {
    const raw = Array.isArray(debugParam) ? debugParam[0] : debugParam;
    if (raw === 'true' || raw === '1') {
      setDebugEnabled(true);
    }
  }, [debugParam, setDebugEnabled]);

  useEffect(() => {
    setTourId(getCurrentTourId() ?? null);
  }, [segments]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const formatError = (err: unknown) => {
      if (err instanceof Error) {
        return { message: err.message, stack: err.stack };
      }
      if (typeof err === 'string') {
        return { message: err };
      }
      try {
        return { message: JSON.stringify(err) };
      } catch {
        return { message: String(err) };
      }
    };

    const handleError = (event: ErrorEvent) => {
      const details = formatError(event.error ?? event.message);
      logToBff(
        'frontend.unhandled.error',
        {
          ...details,
          source: event.filename,
          line: event.lineno,
          column: event.colno,
          userId: user?.id,
          path: window.location?.pathname ?? '',
          search: window.location?.search ?? '',
          href: window.location?.href ?? '',
          userAgent: navigator?.userAgent ?? '',
        },
        'error'
      );
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const details = formatError(event.reason);
      logToBff(
        'frontend.unhandled.rejection',
        {
          ...details,
          userId: user?.id,
          path: window.location?.pathname ?? '',
          search: window.location?.search ?? '',
          href: window.location?.href ?? '',
          userAgent: navigator?.userAgent ?? '',
        },
        'error'
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [logToBff, user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.console || typeof window.console.error !== 'function') {
      return;
    }

    const originalConsoleError = window.console.error.bind(window.console);

    const stringifyConsoleValue = (value: unknown): string => {
      if (value instanceof Error) {
        return `${value.name}: ${value.message}`;
      }
      if (typeof value === 'string') {
        return value;
      }
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    };

    const patchedConsoleError: typeof console.error = (...args: unknown[]) => {
      originalConsoleError(...args);

      try {
        const firstError = args.find((value): value is Error => value instanceof Error);
        logToBff(
          'frontend.console.error',
          {
            summary: args.map(stringifyConsoleValue).join(' | '),
            errorName: firstError?.name,
            errorMessage: firstError?.message,
            stack: firstError?.stack,
            argumentCount: args.length,
            userId: user?.id,
            path: window.location?.pathname ?? '',
            search: window.location?.search ?? '',
            href: window.location?.href ?? '',
            userAgent: navigator?.userAgent ?? '',
          },
          'error'
        );
      } catch {
        // Keep console behavior intact even if forwarding fails.
      }
    };

    window.console.error = patchedConsoleError;
    return () => {
      if (window.console.error === patchedConsoleError) {
        window.console.error = originalConsoleError;
      }
    };
  }, [logToBff, user?.id]);

  useEffect(() => {
    if (!debugEnabled || !user?.token) {
      setDebugTourName(null);
      setDebugRoundName(null);
      setDebugPlayerId(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setDebugLoading(true);
      try {
        const requests: Array<Promise<Response>> = [];
        if (user.id) {
          requests.push(
            fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        if (tourId) {
          requests.push(
            fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        if (roundId) {
          requests.push(
            fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }
        const responses = await Promise.all(requests);
        let resolvedTourName: string | null = null;
        let resolvedRoundName: string | null = null;
        let resolvedPlayerId: string | null = null;
        for (const response of responses) {
          if (!response.ok) continue;
          const payload = (await response.json()) as { name?: string; tourName?: string; courseName?: string; id?: string };
          if (payload.id && !resolvedPlayerId) {
            resolvedPlayerId = payload.id;
          }
          if (payload.tourName || payload.name) {
            resolvedTourName = payload.tourName ?? payload.name ?? null;
          }
          if (payload.courseName || payload.name) {
            resolvedRoundName = payload.courseName ?? payload.name ?? null;
          }
        }
        if (!cancelled) {
          setDebugTourName(resolvedTourName);
          setDebugRoundName(resolvedRoundName);
          setDebugPlayerId(resolvedPlayerId);
        }
      } catch {
        if (!cancelled) {
          setDebugTourName(null);
          setDebugRoundName(null);
          setDebugPlayerId(null);
        }
      } finally {
        if (!cancelled) setDebugLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, debugEnabled, roundId, tourId, user?.id, user?.token]);

  // A "single round" is stored as a hidden tour and has no tour-level views, so the
  // Tour menu item is hidden for it. Mirror BottomMenu's lazy, cached lookup.
  useEffect(() => {
    if (!tourId || !user?.token) return;
    if (tourId in singleRoundTourIds) return;
    let cancelled = false;
    fetch(`${apiBaseUrl}/api/tours/${tourId}`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: Record<string, unknown> | null) => {
        if (cancelled || !data) return;
        const hidden = (data.isHidden as boolean | undefined) ?? (data.IsHidden as boolean | undefined) ?? false;
        setSingleRoundTourIds((prev) => ({ ...prev, [tourId]: hidden }));
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, tourId, singleRoundTourIds, user?.token]);

  useEffect(() => {
    if (!debugOpen) return;
    let cancelled = false;
    const loadCoachMarks = async () => {
      setDebugCoachMarksLoading(true);
      try {
        const states = await getCoachMarkStatesForDebug(apiBaseUrl);
        if (!cancelled) {
          setDebugCoachMarks(
            states.map((state) => ({
              key: state.key,
              shownCount: state.shownCount,
              maxShows: state.maxShows,
            }))
          );
        }
      } catch {
        if (!cancelled) {
          setDebugCoachMarks([]);
        }
      } finally {
        if (!cancelled) {
          setDebugCoachMarksLoading(false);
        }
      }
    };
    void loadCoachMarks();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, debugOpen]);

  const buildRequestId = useCallback(() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }
    return `req-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }, []);

  const buildGuid = useCallback(() => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const random = `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`.padEnd(32, '0').slice(0, 32);
    return `${random.slice(0, 8)}-${random.slice(8, 12)}-${random.slice(12, 16)}-${random.slice(16, 20)}-${random.slice(20, 32)}`;
  }, []);

  const clearBugImage = useCallback(() => {
    setBugReportImage((prev) => {
      if (prev?.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
  }, []);

  const selectBugImageFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) {
        setBugReportError(t('menu.reportBugInvalidImage'));
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        setBugReportError(t('menu.reportBugImageTooLarge'));
        return;
      }
      if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
        setBugReportError(t('menu.reportBugFileUnsupported'));
        return;
      }
      setBugReportError(null);
      setBugReportImage((prev) => {
        if (prev?.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(prev.previewUrl);
        }
        return {
          file,
          fileName: file.name || 'bug-report-image.png',
          contentType: file.type || 'image/png',
          size: file.size,
          previewUrl: URL.createObjectURL(file),
          uploadedUrl: null,
        };
      });
    },
    [t]
  );

  const requestBugImagePresign = useCallback(
    async (file: File): Promise<{ uploadUrl: string; publicUrl: string }> => {
      const ws = getSharedBffWebSocket(apiBaseUrl);
      ws.setAuthToken(user?.token ?? null);
      const requestId = buildGuid();
      return await new Promise((resolve, reject) => {
        let done = false;
        const finish = (fn: () => void) => {
          if (done) return;
          done = true;
          fn();
        };
        const timeout = setTimeout(() => {
          finish(() => {
            unsubscribe();
            reject(new Error('bug.image.presign.timeout'));
          });
        }, 12000);
        const unsubscribe = ws.onMessage((message: BffInboundMessage) => {
          if ((message as { requestId?: string }).requestId !== requestId) return;
          if (message.type === 'bug.image.presign.ack') {
            const payload = message as { uploadUrl?: string; publicUrl?: string };
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              if (!payload.uploadUrl || !payload.publicUrl) {
                reject(new Error('bug.image.presign.invalidAck'));
                return;
              }
              resolve({ uploadUrl: payload.uploadUrl, publicUrl: payload.publicUrl });
            });
          } else if (message.type === 'bug.image.presign.error') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              reject(new Error((message as { message?: string }).message ?? 'bug.image.presign.failed'));
            });
          }
        });
        const sent = ws.sendBugImagePresign({
          requestId,
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
        });
        if (!sent) {
          finish(() => {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error('bug.image.presign.sendFailed'));
          });
        }
      });
    },
    [apiBaseUrl, buildGuid, user?.token]
  );

  const ensureBugImageUploaded = useCallback(async () => {
    if (!bugReportImage) {
      return null;
    }
    if (bugReportImage.uploadedUrl) {
      return bugReportImage.uploadedUrl;
    }
    const { uploadUrl, publicUrl } = await requestBugImagePresign(bugReportImage.file);
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': bugReportImage.contentType || 'image/png' },
      body: bugReportImage.file,
    });
    if (!uploadResponse.ok) {
      throw new Error(`bug.image.upload.failed.${uploadResponse.status}`);
    }
    setBugReportImage((prev) => (prev ? { ...prev, uploadedUrl: publicUrl } : prev));
    return publicUrl;
  }, [bugReportImage, requestBugImagePresign]);

  const loadBugReportRoundContext = useCallback(async () => {
    if (!effectiveRoundId || !user?.token) {
      return undefined;
    }

    const headers = { Authorization: `Bearer ${user.token}` };
    const fetchJsonSafe = async (url: string): Promise<unknown> => {
      try {
        const response = await fetch(url, { headers });
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    };

    const [roundPayload, playersPayload, scoreboardPayload, holeEntriesPayload, holeEventsPayload, gameDefinitionsPayload, tourLeaderboardPayload] =
      await Promise.all([
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/players`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/scoreboard`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-entries`),
        fetchJsonSafe(`${apiBaseUrl}/api/rounds/${effectiveRoundId}/hole-events`),
        fetchJsonSafe(`${apiBaseUrl}/api/game-definitions`),
        tourId ? fetchJsonSafe(`${apiBaseUrl}/api/tours/${tourId}/leaderboard`) : Promise.resolve(null),
      ]);

    const round = (roundPayload ?? {}) as {
      gameDefinitionIds?: string[];
      GameDefinitionIds?: string[];
      status?: string;
      Status?: string;
      name?: string;
      Name?: string;
      courseName?: string;
      CourseName?: string;
    };
    const gameDefinitionIds = round.gameDefinitionIds ?? round.GameDefinitionIds ?? [];

    const gameDefinitions = Array.isArray(gameDefinitionsPayload)
      ? (gameDefinitionsPayload as Array<{ id?: string; name?: string; competitionType?: string }>)
          .filter((item) => !!item?.id && gameDefinitionIds.includes(item.id as string))
          .map((item) => ({
            id: item.id,
            name: item.name,
            competitionType: item.competitionType,
          }))
      : [];

    const playersRaw = Array.isArray((playersPayload as { players?: unknown[] })?.players)
      ? ((playersPayload as { players?: unknown[] }).players as Array<{ id?: string; name?: string }>)
      : Array.isArray(playersPayload)
        ? (playersPayload as Array<{ id?: string; name?: string }>)
        : [];
    const players = playersRaw.map((player) => ({ id: player.id, name: player.name }));

    const holeEntries = Array.isArray(holeEntriesPayload) ? (holeEntriesPayload as Array<Record<string, unknown>>) : [];
    const holeScoringByHole = Object.values(
      holeEntries.reduce<Record<string, { hole: number; entries: Array<Record<string, unknown>> }>>((acc, entry) => {
        const hole =
          (typeof entry.hole === 'number' && Number.isFinite(entry.hole) ? entry.hole : undefined) ??
          (typeof entry.Hole === 'number' && Number.isFinite(entry.Hole) ? entry.Hole : undefined) ??
          (typeof entry.holeNumber === 'number' && Number.isFinite(entry.holeNumber) ? entry.holeNumber : undefined) ??
          (typeof entry.HoleNumber === 'number' && Number.isFinite(entry.HoleNumber) ? entry.HoleNumber : undefined);
        if (!hole) return acc;
        const key = String(hole);
        if (!acc[key]) {
          acc[key] = { hole, entries: [] };
        }
        acc[key].entries.push({
          playerId: entry.playerId ?? entry.PlayerId ?? null,
          playerName: entry.playerName ?? entry.PlayerName ?? null,
          grossStrokes: entry.grossStrokes ?? entry.GrossStrokes ?? entry.grossStroke ?? entry.GrossStroke ?? null,
          pickup: entry.pickup ?? entry.Pickup ?? null,
          scorePoints: entry.scorePoints ?? entry.ScorePoints ?? null,
          roundGamePoints: entry.roundGamePoints ?? entry.RoundGamePoints ?? null,
        });
        return acc;
      }, {})
    ).sort((a, b) => a.hole - b.hole);

    const gameResults =
      (scoreboardPayload as { scores?: unknown[]; Scores?: unknown[] } | null)?.scores ??
      (scoreboardPayload as { scores?: unknown[]; Scores?: unknown[] } | null)?.Scores ??
      (Array.isArray(scoreboardPayload) ? scoreboardPayload : null);

    return {
      roundId: effectiveRoundId,
      roundName: round.name ?? round.Name ?? null,
      courseName: round.courseName ?? round.CourseName ?? null,
      roundStatus: round.status ?? round.Status ?? null,
      playerCount: players.length,
      players,
      gamesIncluded: {
        gameDefinitionIds,
        definitions: gameDefinitions,
      },
      leaderboard: tourLeaderboardPayload ?? undefined,
      gameResults: gameResults ?? undefined,
      scoringPerHole: holeScoringByHole,
      holeEvents: Array.isArray(holeEventsPayload) ? holeEventsPayload : undefined,
    };
  }, [apiBaseUrl, effectiveRoundId, tourId, user?.token]);

  const submitBugReport = useCallback(async () => {
    const description = bugReportDescription.trim();
    if (!description && !bugReportImage) {
      setBugReportError(t('menu.reportBugMissingContent'));
      return;
    }
    setBugReportSubmitting(true);
    setBugReportOpen(false);
    setBugReportError(null);
    try {
      const ws = getSharedBffWebSocket(apiBaseUrl);
      ws.setAuthToken(user?.token ?? null);
      const requestId = buildGuid();
      const imageUrl = await ensureBugImageUploaded();
      const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
      let roundContext: unknown;
      try {
        roundContext = await loadBugReportRoundContext();
      } catch (error) {
        roundContext = {
          error: error instanceof Error ? error.message : String(error),
        };
      }
      const context = {
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        search: typeof window !== 'undefined' ? window.location.search : '',
        roundId: effectiveRoundId ?? undefined,
        tourId: tourId ?? undefined,
        debugEnabled,
        platform: Platform.OS,
        viewport:
          typeof window !== 'undefined'
            ? { width: window.innerWidth, height: window.innerHeight, devicePixelRatio: window.devicePixelRatio }
            : undefined,
        roundContext,
      };

      await new Promise<void>((resolve, reject) => {
        let done = false;
        const finish = (fn: () => void) => {
          if (done) return;
          done = true;
          fn();
        };
        const timeout = setTimeout(() => {
          finish(() => {
            unsubscribe();
            reject(new Error('bug.report.timeout'));
          });
        }, 15000);
        const unsubscribe = ws.onMessage((message: BffInboundMessage) => {
          if ((message as { requestId?: string }).requestId !== requestId) return;
          if (message.type === 'bug.report.ack') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              resolve();
            });
          } else if (message.type === 'bug.report.error') {
            finish(() => {
              clearTimeout(timeout);
              unsubscribe();
              reject(new Error((message as { message?: string }).message ?? 'bug.report.failed'));
            });
          }
        });
        const sent = ws.sendBugReport({
          requestId,
          description: description || undefined,
          imageUrl: imageUrl ?? undefined,
          pageUrl,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          appVersion: typeof process !== 'undefined' ? process.env.EXPO_PUBLIC_APP_VERSION : undefined,
          timestamp: new Date().toISOString(),
          context,
        });
        if (!sent) {
          finish(() => {
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error('bug.report.sendFailed'));
          });
        }
      });

      setBugReportDescription('');
      clearBugImage();
    } catch (error) {
      setBugReportError(
        `${t('menu.reportBugSendFailed')}: ${error instanceof Error ? error.message : String(error)}`
      );
    } finally {
      setBugReportSubmitting(false);
    }
  }, [
    apiBaseUrl,
    bugReportDescription,
    bugReportImage,
    buildGuid,
    clearBugImage,
    debugEnabled,
    effectiveRoundId,
    ensureBugImageUploaded,
    loadBugReportRoundContext,
    t,
    tourId,
    user?.token,
  ]);

  const openBugFilePicker = useCallback(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      setBugReportError(t('menu.reportBugFileUnsupported'));
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      selectBugImageFile(file);
    };
    input.click();
  }, [selectBugImageFile, t]);

  const bugReportImageMeta = useMemo(() => {
    if (!bugReportImage) {
      return '';
    }
    return `${bugReportImage.fileName} (${Math.round(bugReportImage.size / 1024)} KB)`;
  }, [bugReportImage]);

  useUserInfoSync({ apiBaseUrl, user, setUser, buildRequestId });

  // Developer-only "Score all holes" debug action (hamburger menu).
  const isDeveloper = useMemo(() => hasKeycloakRole(user?.token, 'developer'), [user?.token]);
  // "Golf courses" management menu: shown to admins and to anyone who has created a course.
  const isAdmin = useMemo(() => hasKeycloakRole(user?.token, 'admin'), [user?.token]);
  const { courses: manageableCourses } = useManageableCourses(user?.token);
  const canManageCourses = isAdmin || manageableCourses.length > 0;
  const { scoreAllHoles, importAndScoreAllHoles, isRunning: scoreAllHolesRunning, status: scoreAllHolesStatus } = useScoreAllHoles({
    apiBaseUrl,
    token: user?.token,
    roundId: effectiveRoundId,
  });
  const [scoreAllHolesResult, setScoreAllHolesResult] = useState<ScoreAllHolesResult | null>(null);
  const [scoreAllHolesError, setScoreAllHolesError] = useState<string | null>(null);
  const [scoreAllHolesConfigOpen, setScoreAllHolesConfigOpen] = useState(false);
  const [holesToScoreText, setHolesToScoreText] = useState('18');
  const [cleanBeforeImport, setCleanBeforeImport] = useState(false);
  const openScoreAllHoles = useCallback(() => {
    console.info('[score-all-holes] menu click', { roundId: effectiveRoundId, hasToken: !!user?.token });
    setScoreAllHolesResult(null);
    setScoreAllHolesError(null);
    setHolesToScoreText('18');
    setCleanBeforeImport(false);
    setScoreAllHolesConfigOpen(true);
    setLeftMenuOpen(false);
  }, [effectiveRoundId, user?.token]);
  const runScoreAllHoles = useCallback(async () => {
    const parsed = parseInt(holesToScoreText, 10);
    const count = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    setScoreAllHolesConfigOpen(false);
    setScoreAllHolesResult(null);
    setScoreAllHolesError(null);
    try {
      const result = await scoreAllHoles(count);
      setScoreAllHolesResult(result);
    } catch (err) {
      setScoreAllHolesError(err instanceof Error ? err.message : t('menu.scoreAllHolesError'));
    }
  }, [scoreAllHoles, t, holesToScoreText]);
  // Optional: import a fetch-round-scores.py export and replay it onto the round.
  const openScoreAllHolesImport = useCallback(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      setScoreAllHolesConfigOpen(false);
      setScoreAllHolesError(t('menu.reportBugFileUnsupported'));
      return;
    }
    const parsed = parseInt(holesToScoreText, 10);
    const count = Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.json5,application/json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      setScoreAllHolesConfigOpen(false);
      setScoreAllHolesResult(null);
      setScoreAllHolesError(null);
      void (async () => {
        try {
          const text = await file.text();
          const fixture = JSON.parse(text) as ImportFixture;
          const result = await importAndScoreAllHoles(fixture, count, cleanBeforeImport);
          setScoreAllHolesResult(result);
        } catch (err) {
          setScoreAllHolesError(err instanceof Error ? err.message : t('menu.scoreAllHolesImportError'));
        }
      })();
    };
    input.click();
  }, [holesToScoreText, importAndScoreAllHoles, cleanBeforeImport, t]);

  // Developer-only "Round information" debug view (hamburger menu): shows the full round + holes +
  // players + subgames payloads (including ids) in a scrollable modal.
  const { loadRoundInfo, isRunning: roundInfoLoading } = useRoundInfo({
    apiBaseUrl,
    token: user?.token,
    roundId: effectiveRoundId,
  });
  const [roundInfoOpen, setRoundInfoOpen] = useState(false);
  const [roundInfoResult, setRoundInfoResult] = useState<RoundInfoResult | null>(null);
  const [roundInfoError, setRoundInfoError] = useState<string | null>(null);
  const roundInfoCopyText = useMemo(() => {
    if (!roundInfoResult) return '';
    const r = roundInfoResult;
    const lines: string[] = [];
    lines.push(`Round: ${r.name ?? '—'}${r.status ? ` (${r.status})` : ''}`);
    lines.push(`Round id: ${r.roundId}`);
    if (r.tourId) lines.push(`Tour id: ${r.tourId}`);
    lines.push(`Course: ${r.courseName ?? '—'}${r.courseId ? ` (${r.courseId})` : ''}`);
    if (r.playDate) lines.push(`Play date: ${r.playDate}`);
    lines.push(
      `Holes: ${r.holesCount ?? r.holes.length} · Players: ${r.players.length} · Games: ${r.games.length} · Subgames: ${r.subgameCount}`
    );
    lines.push('', 'Games:');
    r.games.forEach((g) =>
      lines.push(`- ${g.name ?? '—'}${g.weight !== null ? ` · ${g.weight}%` : ''} · ${g.id}`)
    );
    lines.push('', 'Players:');
    r.players.forEach((p) => lines.push(`- ${p.name} · SCP ${p.scp ?? '—'} · ${p.id}`));
    if (r.gamePoints.length) {
      lines.push('', 'Game points:');
      r.gamePoints.forEach((game) => {
        lines.push(`${game.gameName}:`);
        game.rows.forEach((row) => lines.push(`  - ${row.playerName}: ${row.points}`));
      });
    }
    lines.push('', 'Holes:');
    r.holes.forEach((h) =>
      lines.push(`- #${h.number} · par ${h.par ?? '—'} · SI ${h.strokeIndex ?? '—'}${h.id ? ` · ${h.id}` : ''}`)
    );
    return lines.join('\n');
  }, [roundInfoResult]);
  const openRoundInfo = useCallback(async () => {
    setLeftMenuOpen(false);
    setRoundInfoResult(null);
    setRoundInfoError(null);
    setRoundInfoOpen(true);
    try {
      const result = await loadRoundInfo();
      setRoundInfoResult(result);
    } catch (err) {
      setRoundInfoError(err instanceof Error ? err.message : t('menu.roundInfoError'));
    }
  }, [loadRoundInfo, t]);
  const copyRoundInfo = useCallback(() => {
    if (!roundInfoCopyText) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(roundInfoCopyText).catch(() => undefined);
    }
  }, [roundInfoCopyText]);

  useEffect(() => {
    if ((isPublicInviteView || isPublicLandingView || topSegment === 'login' || topSegment === 'register') && leftMenuOpen) {
      setLeftMenuOpen(false);
    }
  }, [isPublicInviteView, isPublicLandingView, leftMenuOpen, topSegment]);

  useEffect(() => {
    if (!bugReportOpen) return;
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const onPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items || items.length === 0) {
        return;
      }
      for (const item of Array.from(items)) {
        if (!item.type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (!file) continue;
        event.preventDefault();
        selectBugImageFile(file);
        break;
      }
    };

    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('paste', onPaste);
    };
  }, [bugReportOpen, selectBugImageFile]);

  useEffect(() => {
    return () => {
      clearBugImage();
    };
  }, [clearBugImage]);

  const handleResetCoachMarks = useCallback(async () => {
    await resetCoachMarksForDebug(apiBaseUrl);
    const states = await getCoachMarkStatesForDebug(apiBaseUrl);
    setDebugCoachMarks(
      states.map((state) => ({
        key: state.key,
        shownCount: state.shownCount,
        maxShows: state.maxShows,
      }))
    );
  }, [apiBaseUrl]);

  if (!authChecked) {
    return null;
  }

  const isTourLeaderboardRoute = topSegment === 'tour' && segments[2] === 'leaderboard';
  const isRoundSummaryRoute = topSegment === 'round' && segments[2] === 'summary';
  const isRoundScorecardRoute = topSegment === 'round' && segments[2] === 'scorecard';
  const showBottomMenu =
    !isPublicInviteView &&
    !isPublicLandingView &&
    (isHoleRoute || isTourLeaderboardRoute || isRoundSummaryRoute || isRoundScorecardRoute);
  const canOpenRoundSettings = !!effectiveRoundId && !scorerOnlyMode;
  const roundSettingsPath = effectiveRoundId
    ? `/round/${effectiveRoundId}/roundstart?mode=update${debugEnabled ? '&debug=true' : ''}`
    : '/my-tours';
  const isSingleRoundTour = !!tourId && singleRoundTourIds[tourId] === true;
  const isMobileView = Platform.OS !== 'web' || compactWebHeader;
  const tourPath = tourId
    ? isMobileView
      ? `/my-tours${debugEnabled ? '?debug=true' : ''}`
      : `/tour/${tourId}/edit${debugEnabled ? '?debug=true' : ''}`
    : `/my-tours${debugEnabled ? '?debug=true' : ''}`;

  return (
    <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <View style={styles.shell}>
        <View style={styles.stack}>
          <ServiceStatusModal
            open={serviceStatusOpen}
            onClose={() => setServiceStatusOpen(false)}
            missing={serviceStatusMissing}
            checkedAt={serviceStatusCheckedAt}
            hasWarnings={serviceStatusHasWarnings}
            warnings={serviceStatusWarnings}
          />
          <DebugModal
            open={debugOpen}
            onClose={() => setDebugOpen(false)}
            user={user}
            playerId={debugPlayerId}
            roundId={roundId}
            tourId={tourId}
            roundName={debugRoundName}
            tourName={debugTourName}
            loading={debugLoading}
            coachMarks={debugCoachMarks}
            coachMarksLoading={debugCoachMarksLoading}
            onDisable={() => {
              setDebugEnabled(false);
              setDebugOpen(false);
            }}
            onResetCoachMarks={handleResetCoachMarks}
          />
          <Modal
            transparent
            visible={!isPublicInviteView && leftMenuOpen}
            animationType="fade"
            onRequestClose={() => setLeftMenuOpen(false)}
          >
            <LeftMenu
              open={leftMenuOpen}
              isHoleRoute={isHoleRoute}
              canOpenRoundSettings={canOpenRoundSettings}
              roundSettingsPath={roundSettingsPath}
              scorerOnlyMode={scorerOnlyMode}
              isSingleRound={isSingleRoundTour}
              mysteryPath={effectiveRoundId ? `/round/${effectiveRoundId}/mystery-prize?tourId=${tourId ?? ''}&debug=${debugEnabled ? 'true' : 'false'}` : '/round'}
              tourPath={tourPath}
              canManageCourses={canManageCourses}
              isDeveloper={isDeveloper}
              scoreAllHolesEnabled={isDeveloper}
              scoreAllHolesRunning={scoreAllHolesRunning}
              scoreAllHolesStatus={scoreAllHolesStatus}
              onScoreAllHoles={openScoreAllHoles}
              roundInfoEnabled={isDeveloper && !!effectiveRoundId}
              roundInfoLoading={roundInfoLoading}
              onRoundInfo={openRoundInfo}
              onOpenBugReport={() => {
                setLeftMenuOpen(false);
                setBugReportError(null);
                setBugReportOpen(true);
              }}
              onClose={() => setLeftMenuOpen(false)}
            />
          </Modal>
          <Modal
            transparent
            visible={scoreAllHolesConfigOpen || scoreAllHolesRunning || !!scoreAllHolesResult || !!scoreAllHolesError}
            animationType="fade"
            onRequestClose={() => {
              if (scoreAllHolesRunning) return;
              setScoreAllHolesConfigOpen(false);
              setScoreAllHolesResult(null);
              setScoreAllHolesError(null);
            }}
          >
            <View style={styles.leftMenuBackdrop}>
              <View style={styles.bugModalCard}>
                <Text style={styles.bugModalTitle}>{t('menu.scoreAllHoles')}</Text>
                {scoreAllHolesConfigOpen ? (
                  <>
                    <Text style={styles.bugModalHint}>{t('menu.scoreAllHolesCountLabel')}</Text>
                    <TextInput
                      value={holesToScoreText}
                      onChangeText={setHolesToScoreText}
                      keyboardType="number-pad"
                      style={styles.scoreModalInput}
                      placeholder="18"
                      placeholderTextColor={appColors.text.muted}
                    />
                    <Pressable
                      style={styles.scoreModalCheckboxRow}
                      onPress={() => setCleanBeforeImport((prev) => !prev)}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: cleanBeforeImport }}
                    >
                      <View style={[styles.scoreModalCheckbox, cleanBeforeImport && styles.scoreModalCheckboxChecked]}>
                        {cleanBeforeImport ? <Text style={styles.scoreModalCheckboxMark}>✓</Text> : null}
                      </View>
                      <Text style={styles.scoreModalCheckboxLabel}>{t('menu.scoreAllHolesClean')}</Text>
                    </Pressable>
                    <Text style={styles.scoreModalImportHint}>{t('menu.scoreAllHolesImportHint')}</Text>
                    <View style={styles.scoreModalButtonRow}>
                      <Pressable style={styles.scoreModalSecondaryButton} onPress={() => setScoreAllHolesConfigOpen(false)}>
                        <Text style={styles.scoreModalOkText}>{t('menu.close')}</Text>
                      </Pressable>
                      <Pressable style={styles.scoreModalSecondaryButton} onPress={openScoreAllHolesImport}>
                        <Text style={styles.scoreModalOkText}>{t('menu.scoreAllHolesImport')}</Text>
                      </Pressable>
                      <Pressable style={styles.scoreModalOkButton} onPress={runScoreAllHoles}>
                        <Text style={styles.scoreModalOkText}>{t('menu.scoreAllHolesStart')}</Text>
                      </Pressable>
                    </View>
                  </>
                ) : scoreAllHolesError ? (
                  <>
                    <Text style={styles.scoreModalError}>{scoreAllHolesError}</Text>
                    <Pressable
                      style={styles.scoreModalOkButton}
                      onPress={() => {
                        setScoreAllHolesResult(null);
                        setScoreAllHolesError(null);
                      }}
                    >
                      <Text style={styles.scoreModalOkText}>{t('menu.ok')}</Text>
                    </Pressable>
                  </>
                ) : !scoreAllHolesResult ? (
                  <Text style={styles.bugModalHint}>{scoreAllHolesStatus ?? t('menu.scoreAllHolesRunning')}</Text>
                ) : (
                  <>
                    <Text style={styles.bugModalHint}>
                      {t('menu.scoreAllHolesDone', {
                        scores: scoreAllHolesResult.summary.scores,
                        holes: scoreAllHolesResult.summary.holes,
                        players: scoreAllHolesResult.summary.players,
                        events: scoreAllHolesResult.summary.events,
                        mystery: scoreAllHolesResult.summary.mystery,
                      })}
                    </Text>
                    <ScrollView style={styles.scoreModalScroll}>
                      {scoreAllHolesResult.holes.map((hole) => (
                        <View key={`score-hole-${hole.hole}`} style={styles.scoreModalHole}>
                          <Text style={styles.scoreModalHoleTitle}>
                            {t('menu.scoreAllHolesHole', { hole: hole.hole, par: hole.par })}
                          </Text>
                          {hole.scores.map((score) => {
                            const suffix = score.events.length ? ` · ${score.events.join(', ')}` : '';
                            const line = `${t('menu.scoreAllHolesRow', { name: score.playerName, strokes: score.strokes })}${suffix}`;
                            return (
                              <Text key={`score-hole-${hole.hole}-${score.playerId}`} style={styles.scoreModalRow}>
                                {line}
                              </Text>
                            );
                          })}
                        </View>
                      ))}
                      {scoreAllHolesResult.mystery.length ? (
                        <View style={styles.scoreModalHole}>
                          <Text style={styles.scoreModalHoleTitle}>{t('menu.scoreAllHolesMystery')}</Text>
                          {scoreAllHolesResult.mystery.map((entry, index) => (
                            <Text key={`score-mystery-${index}`} style={styles.scoreModalRow}>
                              {t('menu.scoreAllHolesMysteryRow', { hole: entry.hole, name: entry.playerName })}
                            </Text>
                          ))}
                        </View>
                      ) : null}
                    </ScrollView>
                    <Pressable
                      style={styles.scoreModalOkButton}
                      onPress={() => {
                        setScoreAllHolesResult(null);
                        setScoreAllHolesError(null);
                      }}
                    >
                      <Text style={styles.scoreModalOkText}>{t('menu.ok')}</Text>
                    </Pressable>
                  </>
                )}
              </View>
            </View>
          </Modal>
          <Modal
            transparent
            visible={roundInfoOpen}
            animationType="fade"
            onRequestClose={() => setRoundInfoOpen(false)}
          >
            <View style={styles.leftMenuBackdrop}>
              <View style={styles.bugModalCard}>
                <Text style={styles.bugModalTitle}>{t('menu.roundInfo')}</Text>
                {roundInfoError ? (
                  <Text style={styles.scoreModalError}>{roundInfoError}</Text>
                ) : !roundInfoResult ? (
                  <Text style={styles.bugModalHint}>{t('menu.roundInfoLoading')}</Text>
                ) : (
                  <ScrollView style={styles.scoreModalScroll}>
                    <Text selectable style={styles.roundInfoHeading}>
                      {roundInfoResult.name ?? '—'}
                      {roundInfoResult.status ? ` · ${roundInfoResult.status}` : ''}
                    </Text>
                    <View style={styles.scoreModalHole}>
                      <Text selectable style={styles.roundInfoRow}>{`Round id: ${roundInfoResult.roundId}`}</Text>
                      {roundInfoResult.tourId ? (
                        <Text selectable style={styles.roundInfoRow}>{`Tour id: ${roundInfoResult.tourId}`}</Text>
                      ) : null}
                      <Text selectable style={styles.roundInfoRow}>
                        {`Course: ${roundInfoResult.courseName ?? '—'}${roundInfoResult.courseId ? `  (${roundInfoResult.courseId})` : ''}`}
                      </Text>
                      {roundInfoResult.playDate ? (
                        <Text selectable style={styles.roundInfoRow}>{`Play date: ${roundInfoResult.playDate}`}</Text>
                      ) : null}
                      <Text selectable style={styles.roundInfoRow}>
                        {`Holes: ${roundInfoResult.holesCount ?? roundInfoResult.holes.length}  ·  Players: ${roundInfoResult.players.length}  ·  Games: ${roundInfoResult.games.length}  ·  Subgames: ${roundInfoResult.subgameCount}`}
                      </Text>
                    </View>

                    <Text style={styles.scoreModalHoleTitle}>{`Games (${roundInfoResult.games.length})`}</Text>
                    <View style={styles.scoreModalHole}>
                      {roundInfoResult.games.length === 0 ? (
                        <Text style={styles.roundInfoRow}>—</Text>
                      ) : (
                        roundInfoResult.games.map((game) => (
                          <Text key={`round-info-game-${game.id}`} selectable style={styles.roundInfoRow}>
                            {`${game.name ?? '—'}${game.weight !== null ? `  ·  ${game.weight}%` : ''}  ·  ${game.id}`}
                          </Text>
                        ))
                      )}
                    </View>

                    <Text style={styles.scoreModalHoleTitle}>{`Players (${roundInfoResult.players.length})`}</Text>
                    <View style={styles.scoreModalHole}>
                      {roundInfoResult.players.map((player) => (
                        <Text key={`round-info-player-${player.id}`} selectable style={styles.roundInfoRow}>
                          {`${player.name}  ·  SCP ${player.scp ?? '—'}  ·  ${player.id}`}
                        </Text>
                      ))}
                    </View>

                    <Text style={styles.scoreModalHoleTitle}>{`Game points (${roundInfoResult.gamePoints.length})`}</Text>
                    <View style={styles.scoreModalHole}>
                      {roundInfoResult.gamePoints.length === 0 ? (
                        <Text style={styles.roundInfoRow}>—</Text>
                      ) : (
                        roundInfoResult.gamePoints.map((game) => (
                          <View key={`round-info-gamepoints-${game.gameId}`} style={styles.roundInfoGameBlock}>
                            <Text selectable style={styles.roundInfoGameName}>{game.gameName}</Text>
                            {game.rows.map((row) => (
                              <Text
                                key={`round-info-gamepoints-${game.gameId}-${row.playerId}`}
                                selectable
                                style={styles.roundInfoRow}
                              >
                                {`${row.playerName}: ${row.points}`}
                              </Text>
                            ))}
                          </View>
                        ))
                      )}
                    </View>

                    <Text style={styles.scoreModalHoleTitle}>{`Holes (${roundInfoResult.holes.length})`}</Text>
                    <View style={styles.scoreModalHole}>
                      {roundInfoResult.holes.map((hole) => (
                        <Text key={`round-info-hole-${hole.number}`} selectable style={styles.roundInfoRow}>
                          {`#${hole.number}  ·  par ${hole.par ?? '—'}  ·  SI ${hole.strokeIndex ?? '—'}${hole.id ? `  ·  ${hole.id}` : ''}`}
                        </Text>
                      ))}
                    </View>
                  </ScrollView>
                )}
                <View style={styles.scoreModalButtonRow}>
                  {roundInfoResult ? (
                    <Pressable style={styles.scoreModalSecondaryButton} onPress={copyRoundInfo}>
                      <Text style={styles.scoreModalOkText}>{t('menu.roundInfoCopy')}</Text>
                    </Pressable>
                  ) : null}
                  <Pressable style={styles.scoreModalOkButton} onPress={() => setRoundInfoOpen(false)}>
                    <Text style={styles.scoreModalOkText}>{t('menu.close')}</Text>
                  </Pressable>
                </View>
              </View>
            </View>
          </Modal>
          <Modal
            transparent
            visible={bugReportOpen}
            animationType="fade"
            onRequestClose={() => setBugReportOpen(false)}
          >
            <Pressable style={styles.leftMenuBackdrop} onPress={() => setBugReportOpen(false)}>
              <Pressable style={styles.bugModalCard} onPress={() => null}>
                <Text style={styles.bugModalTitle}>{t('menu.reportBugTitle')}</Text>
                <Text style={styles.bugModalHint}>{t('menu.reportBugHint')}</Text>
                <TextInput
                  value={bugReportDescription}
                  onChangeText={setBugReportDescription}
                  multiline
                  numberOfLines={5}
                  style={styles.bugModalInput}
                  placeholder={t('menu.reportBugDescriptionPlaceholder')}
                  placeholderTextColor={appColors.text.muted}
                  editable={!bugReportSubmitting}
                />
                <View style={styles.bugModalRow}>
                  <Pressable
                    style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={openBugFilePicker}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{t('menu.reportBugAddImage')}</Text>
                  </Pressable>
                  {bugReportImage ? (
                    <Pressable
                      style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                      onPress={clearBugImage}
                      disabled={bugReportSubmitting}
                    >
                      <Text style={styles.bugModalButtonText}>{t('menu.reportBugRemoveImage')}</Text>
                    </Pressable>
                  ) : null}
                </View>
                {bugReportImage ? (
                  <View style={styles.bugPreviewCard}>
                    <Image source={{ uri: bugReportImage.previewUrl }} style={styles.bugPreviewImage} resizeMode="cover" />
                    <Text style={styles.bugPreviewMeta}>{bugReportImageMeta}</Text>
                  </View>
                ) : (
                  <Text style={styles.bugModalHint}>{t('menu.reportBugPasteHint')}</Text>
                )}
                {bugReportError ? <Text style={styles.bugModalError}>{bugReportError}</Text> : null}
                <View style={styles.bugModalRow}>
                  <Pressable
                    style={[styles.bugModalButton, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={() => setBugReportOpen(false)}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{t('menu.close')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.bugModalButtonPrimary, bugReportSubmitting && styles.leftMenuItemDisabled]}
                    onPress={submitBugReport}
                    disabled={bugReportSubmitting}
                  >
                    <Text style={styles.bugModalButtonText}>{bugReportSubmitting ? t('menu.reportBugSending') : t('menu.reportBugSend')}</Text>
                  </Pressable>
                </View>
              </Pressable>
            </Pressable>
          </Modal>
          <Stack
            screenOptions={{
              headerStyle: {
                backgroundColor: appColors.background.app,
              },
              headerShadowVisible: false,
              headerTitleAlign: 'center',
              headerTintColor: appColors.text.heading,
              headerTitleStyle: {
                color: appColors.text.heading,
                fontSize: 30,
                fontWeight: '700',
                letterSpacing: 0.2,
                ...Platform.select({
                  web: { fontFamily: 'Newsreader, Playfair Display, Georgia, serif' },
                  default: { fontFamily: 'serif' },
                }),
              },
              contentStyle: {
                backgroundColor: appColors.background.app,
              },
              headerLeft: () =>
                isPublicInviteView || scorerOnlyMode || isAnonymousMode ? null : (
                  <Pressable style={styles.headerIconButton} onPress={() => setLeftMenuOpen((prev) => !prev)}>
                    <View style={styles.headerMenuIcon} accessibilityRole="image">
                      <View style={styles.headerMenuBar} />
                      <View style={styles.headerMenuBar} />
                      <View style={styles.headerMenuBar} />
                    </View>
                  </Pressable>
                ),
              headerRight: () => (
                isPublicInviteView ? (
                  <Pressable
                    style={[styles.loginButton, compactWebHeader && styles.loginButtonCompact]}
                    onPress={() => {
                      void handleRedirect();
                    }}
                    accessibilityLabel={t('auth.loginTitle')}
                  >
                    <Text style={styles.loginButtonText}>{t('auth.loginTitle')}</Text>
                  </Pressable>
                ) : (
                  <View style={[styles.headerRight, compactWebHeader && styles.headerRightCompact]}>
                    {debugEnabled && !compactWebHeader ? (
                      <Pressable
                        style={styles.debugButton}
                        onPress={() => setDebugOpen(true)}
                        accessibilityLabel={t('debug.buttonLabel')}
                      >
                        <Text style={styles.debugButtonText}>{t('debug.button')}</Text>
                      </Pressable>
                    ) : null}
                    {canCheckServiceHealth && !compactWebHeader ? (
                      serviceStatusMissing.length > 0 || serviceStatusFetchFailed ? (
                        <Pressable
                          style={[
                            styles.serviceStatusButtonCritical,
                            serviceStatusFetchFailed && !serviceStatusFlashOn ? styles.serviceStatusButtonCriticalDim : null,
                          ]}
                          onPress={() => setServiceStatusOpen(true)}
                          accessibilityLabel={t('serviceStatus.buttonLabel')}
                        >
                          <Text style={styles.serviceStatusButtonText}>{t('serviceStatus.alert')}</Text>
                        </Pressable>
                      ) : serviceStatusHasWarnings ? (
                        <Pressable
                          style={styles.serviceStatusButtonWarning}
                          onPress={() => setServiceStatusOpen(true)}
                          accessibilityLabel={t('serviceStatus.buttonLabel')}
                        >
                          <Text style={styles.serviceStatusButtonText}>{t('serviceStatus.alert')}</Text>
                        </Pressable>
                      ) : null
                    ) : null}
                    <HeaderUser
                      compact={compactWebHeader}
                      hideProfileAndFriends={isAnonymousMode}
                      onOpenBugReport={() => {
                        setBugReportError(null);
                        setBugReportOpen(true);
                      }}
                    />
                  </View>
                )
              ),
            }}
          >
            <Stack.Screen name="index" options={{ href: null, headerShown: false }} />
            <Stack.Screen name="login" options={{ title: t('auth.loginTitle'), headerShown: false }} />
            <Stack.Screen name="register" options={{ title: t('register.title'), headerShown: false }} />
            <Stack.Screen name="welcome" options={{ title: t('welcome.title') }} />
            <Stack.Screen
              name="my-tours"
              options={{
                title: t('welcome.myToursNav'),
                headerTitleContainerStyle: {
                  left: 5,
                },
              }}
            />
            <Stack.Screen name="friends" options={{ title: t('friends.title') }} />
            <Stack.Screen name="modal" options={{ presentation: 'modal', title: t('modal.title') }} />
            <Stack.Screen name="debug/deeplink" options={{ title: t('debug.deeplinkTitle') }} />
            <Stack.Screen name="round/[roundId]/select-games" options={{ title: t('selectGames.title') }} />
            <Stack.Screen name="round/[roundId]/roundstart" options={{ title: t('roundStartSetup.title') }} />
            <Stack.Screen name="round/[roundId]/mystery-prize" options={{ title: t('mysteryPrize.title') }} />
            <Stack.Screen name="round/[roundId]/hole/[hole]/compact" options={{ title: t('hole.title'), animation: 'slide_from_right' }} />
            <Stack.Screen name="tour/[tourId]/leaderboard" options={{ title: t('tourLeaderboard.title') }} />
            <Stack.Screen name="tour/invite/[inviteToken]" options={{ title: t('tourInvite.title') }} />
            <Stack.Screen name="round/[roundId]/summary" options={{ title: t('roundResults.title') }} />
            <Stack.Screen name="tour/[tourId]/round-init" options={{ title: t('players.roundInitTitle') }} />
            <Stack.Screen name="round/[roundId]/scorecard" options={{ title: t('scorecard.title') }} />
            <Stack.Screen
              name="sheet/tour/[tourId]/leaderboard"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen
              name="sheet/round/[roundId]/summary"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen
              name="sheet/round/[roundId]/scorecard"
              options={{
                headerShown: false,
                presentation: 'transparentModal',
                animation: 'none',
                contentStyle: { backgroundColor: 'transparent' },
              }}
            />
            <Stack.Screen name="tour/[tourId]/edit" options={{ headerShown: false }} />
            <Stack.Screen name="profile/index" options={{ title: t('profile.title') }} />
            <Stack.Screen name="config-course" options={{ title: t('courseConfig.title'), presentation: 'modal' }} />
          </Stack>
        </View>
        {showBottomMenu ? <BottomMenu /> : null}
      </View>
    </ThemeProvider>
  );
}

type LeftMenuProps = {
  open: boolean;
  isHoleRoute: boolean;
  canOpenRoundSettings: boolean;
  roundSettingsPath: string;
  scorerOnlyMode: boolean;
  isSingleRound: boolean;
  mysteryPath: string;
  tourPath: string;
  canManageCourses: boolean;
  isDeveloper: boolean;
  scoreAllHolesEnabled: boolean;
  scoreAllHolesRunning: boolean;
  scoreAllHolesStatus: string | null;
  onScoreAllHoles: () => void;
  roundInfoEnabled: boolean;
  roundInfoLoading: boolean;
  onRoundInfo: () => void;
  onClose: () => void;
};

const LeftMenu = ({
  open,
  isHoleRoute,
  canOpenRoundSettings,
  roundSettingsPath,
  scorerOnlyMode,
  isSingleRound,
  mysteryPath,
  tourPath,
  canManageCourses,
  isDeveloper,
  scoreAllHolesEnabled,
  scoreAllHolesRunning,
  scoreAllHolesStatus,
  onScoreAllHoles,
  roundInfoEnabled,
  roundInfoLoading,
  onRoundInfo,
  onClose,
}: LeftMenuProps) => {
  const router = useRouter();
  const { t } = useI18n();

  if (!open) return null;

  return (
    <Pressable style={styles.leftMenuBackdrop} onPress={onClose}>
      <View style={styles.leftMenuCard}>
        <Pressable
          style={styles.leftMenuItem}
          onPress={() => {
            onClose();
            router.push('/welcome');
          }}
        >
          <Text style={styles.leftMenuItemText}>{t('menu.home')}</Text>
        </Pressable>
        <Pressable
          style={[styles.leftMenuItem, !canOpenRoundSettings && styles.leftMenuItemDisabled]}
          disabled={!canOpenRoundSettings}
          onPress={() => {
            onClose();
            router.push(roundSettingsPath);
          }}
        >
          <Text style={[styles.leftMenuItemText, !canOpenRoundSettings && styles.leftMenuItemTextDisabled]}>
            {t('tournaments.roundSettings')}
          </Text>
        </Pressable>
        <Pressable
          style={styles.leftMenuItem}
          onPress={() => {
            onClose();
            router.push(mysteryPath);
          }}
        >
          <Text style={styles.leftMenuItemText}>{t('mysteryPrize.title')}</Text>
        </Pressable>
        {!isSingleRound ? (
          <Pressable
            style={[styles.leftMenuItem, scorerOnlyMode && styles.leftMenuItemDisabled]}
            disabled={scorerOnlyMode}
            onPress={() => {
              onClose();
              router.push(tourPath);
            }}
          >
            <Text style={[styles.leftMenuItemText, scorerOnlyMode && styles.leftMenuItemTextDisabled]}>{t('menu.tour')}</Text>
          </Pressable>
        ) : null}
        {canManageCourses ? (
          <Pressable
            style={styles.leftMenuItem}
            onPress={() => {
              onClose();
              router.push('/config-course?manage=1');
            }}
          >
            <Text style={styles.leftMenuItemText}>{t('menu.golfCourses')}</Text>
          </Pressable>
        ) : null}
        {isDeveloper ? (
          <>
            <Pressable
              style={[styles.leftMenuItem, (!scoreAllHolesEnabled || scoreAllHolesRunning) && styles.leftMenuItemDisabled]}
              disabled={!scoreAllHolesEnabled || scoreAllHolesRunning}
              onPress={onScoreAllHoles}
            >
              <Text
                style={[
                  styles.leftMenuItemText,
                  (!scoreAllHolesEnabled || scoreAllHolesRunning) && styles.leftMenuItemTextDisabled,
                ]}
              >
                {scoreAllHolesRunning ? t('menu.scoreAllHolesRunning') : t('menu.scoreAllHoles')}
              </Text>
            </Pressable>
            {scoreAllHolesStatus ? <Text style={styles.leftMenuHint}>{scoreAllHolesStatus}</Text> : null}
            <Pressable
              style={[styles.leftMenuItem, (!roundInfoEnabled || roundInfoLoading) && styles.leftMenuItemDisabled]}
              disabled={!roundInfoEnabled || roundInfoLoading}
              onPress={onRoundInfo}
            >
              <Text
                style={[
                  styles.leftMenuItemText,
                  (!roundInfoEnabled || roundInfoLoading) && styles.leftMenuItemTextDisabled,
                ]}
              >
                {roundInfoLoading ? t('menu.roundInfoLoading') : t('menu.roundInfo')}
              </Text>
            </Pressable>
          </>
        ) : null}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  stack: {
    flex: 1,
  },
  headerIconButton: {
    marginLeft: 12,
    paddingHorizontal: 8,
    paddingVertical: 10,
  },
  headerMenuIcon: {
    width: 22,
    gap: 4,
  },
  headerMenuBar: {
    height: 2,
    borderRadius: 999,
    backgroundColor: appColors.text.heading,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  headerRightCompact: {
    gap: 4,
  },
  loginButton: {
    marginRight: 8,
    backgroundColor: appColors.surface.alt,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.default,
    flexShrink: 1,
  },
  loginButtonCompact: {
    marginRight: 0,
    paddingHorizontal: 10,
  },
  loginButtonText: {
    color: appColors.text.secondary,
    fontWeight: '800',
    fontSize: 12,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  serviceStatusButtonCritical: {
    backgroundColor: '#d7263d',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#f08a95',
  },
  serviceStatusButtonCriticalDim: {
    opacity: 0.35,
  },
  serviceStatusButtonWarning: {
    backgroundColor: '#e68a00',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#d1d27a',
  },
  serviceStatusButtonText: {
    color: '#d8f3f4',
    fontWeight: '700',
  },
  debugButton: {
    backgroundColor: appColors.surface.alt,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.default,
  },
  debugButtonText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 1.4,
    textTransform: 'uppercase',
  },
  leftMenuBackdrop: {
    flex: 1,
    backgroundColor: appColors.accent.overlay,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
      },
    }),
  },
  leftMenuCard: {
    position: 'absolute',
    top: 52,
    left: 12,
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    minWidth: 170,
    shadowColor: '#C8C8B0',
    shadowOpacity: 0.3,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
  leftMenuItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  leftMenuItemDisabled: {
    opacity: 0.45,
  },
  leftMenuItemText: {
    color: appColors.text.primary,
    fontWeight: '600',
    fontSize: 14,
  },
  leftMenuItemTextDisabled: {
    color: appColors.text.muted,
  },
  leftMenuHint: {
    color: appColors.text.muted,
    fontSize: 12,
    paddingHorizontal: 14,
    paddingBottom: 8,
  },
  scoreModalScroll: {
    marginTop: 10,
    maxHeight: 360,
  },
  scoreModalHole: {
    marginBottom: 10,
  },
  scoreModalHoleTitle: {
    color: appColors.text.heading,
    fontWeight: '700',
    fontSize: 14,
    marginBottom: 2,
  },
  scoreModalRow: {
    color: appColors.text.primary,
    fontSize: 13,
    paddingLeft: 8,
  },
  roundInfoHeading: {
    color: appColors.text.heading,
    fontWeight: '700',
    fontSize: 16,
    marginBottom: 6,
  },
  roundInfoRow: {
    color: appColors.text.primary,
    fontSize: 12,
    lineHeight: 17,
    paddingLeft: 8,
  },
  roundInfoGameBlock: {
    marginBottom: 6,
  },
  roundInfoGameName: {
    color: appColors.text.heading,
    fontSize: 12,
    fontWeight: '600',
  },
  scoreModalError: {
    color: appColors.status.danger,
    fontSize: 14,
    marginTop: 10,
  },
  scoreModalInput: {
    backgroundColor: appColors.background.panelAlt,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    color: appColors.text.primary,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 8,
  },
  scoreModalButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
  },
  scoreModalCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 12,
  },
  scoreModalCheckbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.surface.input,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreModalCheckboxChecked: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.border.accent,
  },
  scoreModalCheckboxMark: {
    color: appColors.text.inverse,
    fontWeight: '800',
    fontSize: 14,
    lineHeight: 16,
  },
  scoreModalCheckboxLabel: {
    color: appColors.text.primary,
    fontSize: 13,
    flexShrink: 1,
  },
  scoreModalImportHint: {
    color: appColors.text.muted,
    fontSize: 12,
    lineHeight: 17,
    marginTop: 12,
  },
  scoreModalSecondaryButton: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 9,
    marginTop: 14,
  },
  scoreModalOkButton: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.accent.positive,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 9,
    marginTop: 14,
  },
  scoreModalOkText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 14,
  },
  bugModalCard: {
    position: 'absolute',
    top: 80,
    left: 16,
    right: 16,
    maxWidth: 520,
    marginHorizontal: 'auto',
    maxHeight: '82%',
    backgroundColor: appColors.background.panel,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.default,
    padding: 14,
    gap: 10,
  },
  bugModalTitle: {
    color: appColors.text.heading,
    fontSize: 18,
    fontWeight: '800',
  },
  bugModalHint: {
    color: appColors.text.secondary,
    fontSize: 12,
  },
  bugModalInput: {
    minHeight: 110,
    backgroundColor: appColors.background.panelAlt,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    color: appColors.text.primary,
    paddingHorizontal: 10,
    paddingVertical: 8,
    textAlignVertical: 'top',
  },
  bugModalRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  bugModalButton: {
    backgroundColor: appColors.background.interactiveMuted,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  bugModalButtonPrimary: {
    backgroundColor: appColors.background.interactive,
    borderColor: appColors.accent.positive,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 'auto',
  },
  bugModalButtonText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  bugPreviewCard: {
    borderRadius: 12,
    borderColor: appColors.border.strong,
    borderWidth: 1,
    backgroundColor: appColors.background.panelAlt,
    padding: 8,
    gap: 6,
  },
  bugPreviewImage: {
    width: '100%',
    height: 180,
    borderRadius: 8,
    backgroundColor: appColors.background.app,
  },
  bugPreviewMeta: {
    color: appColors.text.secondary,
    fontSize: 11,
  },
  bugModalError: {
    color: '#ff9ca8',
    fontSize: 12,
    fontWeight: '600',
  },
});
